import { useState } from "react";
import { Link } from "react-router-dom";
import { useAccount } from "wagmi";
import { useCreateMarket } from "@/hooks/useCreateMarket";

export default function CreateMarketPage() {
  const { isConnected } = useAccount();
  const { createMarket, isPending, isSuccess, error } = useCreateMarket();

  const [question, setQuestion] = useState("");
  const [durationDays, setDurationDays] = useState("7");
  const [durationHours, setDurationHours] = useState("0");

  const questionLength = question.length;
  const isValidQuestion = questionLength >= 10 && questionLength <= 500;

  const parsedDays = parseInt(durationDays) || 0;
  const parsedHours = parseInt(durationHours) || 0;
  const totalHours = parsedDays * 24 + parsedHours;
  const isValidDuration = totalHours >= 1 && totalHours <= 365 * 24;

  const endTime = BigInt(Math.floor(Date.now() / 1000) + totalHours * 60 * 60);
  const endDate = new Date(Number(endTime) * 1000);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isValidQuestion && isValidDuration) {
      createMarket(question, endTime);
    }
  };

  if (isSuccess) {
    return (
      <div className="max-w-xl mx-auto">
        <div className="card p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Market Created!</h2>
          <p className="text-gray-600 mb-6">Your prediction market has been created successfully.</p>
          <div className="flex gap-4 justify-center">
            <Link to="/" className="btn btn-primary">
              View Markets
            </Link>
            <button onClick={() => window.location.reload()} className="btn btn-outline">
              Create Another
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!isConnected) {
    return (
      <div className="max-w-xl mx-auto">
        <div className="card p-8 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Create a Market</h2>
          <p className="text-gray-600 mb-6">Connect your wallet to create a prediction market.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-xl mx-auto">
      {/* Back Link */}
      <Link to="/" className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-4">
        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Back to Markets
      </Link>

      <div className="card p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">Create a Market</h1>

        <form onSubmit={handleSubmit}>
          {/* Question Input */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Market Question
            </label>
            <textarea
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="Will JPMorgan Chase beat consensus EPS estimates in its Q2 2026 earnings report?"
              rows={3}
              className="input resize-none"
              maxLength={500}
            />
            <div className="flex justify-between mt-1">
              <span className={`text-xs ${
                questionLength < 10 ? "text-red-500" : "text-gray-500"
              }`}>
                {questionLength < 10 ? `${10 - questionLength} more characters needed` : ""}
              </span>
              <span className={`text-xs ${
                questionLength > 450 ? "text-yellow-600" : "text-gray-500"
              }`}>
                {questionLength}/500
              </span>
            </div>
          </div>

          {/* Duration */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Market Duration
            </label>
            <div className="flex gap-4">
              <div className="flex-1">
                <div className="relative">
                  <input
                    type="number"
                    value={durationDays}
                    onChange={(e) => setDurationDays(e.target.value)}
                    min="0"
                    max="365"
                    className="input"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">
                    days
                  </span>
                </div>
              </div>
              <div className="flex-1">
                <div className="relative">
                  <input
                    type="number"
                    value={durationHours}
                    onChange={(e) => setDurationHours(e.target.value)}
                    min="0"
                    max="23"
                    className="input"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">
                    hours
                  </span>
                </div>
              </div>
            </div>
            {isValidDuration && (
              <p className="text-sm text-gray-500 mt-2">
                Predictions close: {endDate.toLocaleString()}
              </p>
            )}
            {!isValidDuration && (
              <p className="text-sm text-red-500 mt-2">
                Duration must be between 1 hour and 365 days
              </p>
            )}
          </div>

          {/* Preview */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Preview</h3>
            <div className="text-gray-900">
              {question || "Your question will appear here..."}
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="bg-red-50 text-red-700 p-3 rounded-lg mb-4 text-sm">
              {error.message || "An error occurred while creating the market."}
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={!isValidQuestion || !isValidDuration || isPending}
            className="btn btn-primary w-full"
          >
            {isPending ? "Creating Market..." : "Create Market"}
          </button>
        </form>

        {/* Info */}
        <div className="mt-6 p-4 bg-blue-50 rounded-lg">
          <h4 className="text-sm font-medium text-blue-900 mb-2">How it works</h4>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>1. Create a yes/no question about a future event</li>
            <li>2. Users stake USDT on their predicted outcome</li>
            <li>3. After the event, the market is resolved</li>
            <li>4. Winners claim their proportional share of the pool</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
